import type { Shop, ShopAttribute } from './types.js';
import { config } from '../config/environment.js';

function normalizeAttribute(raw: unknown, iconMap: Record<string, string>): ShopAttribute {
  if (!raw || typeof raw !== 'object') return {};
  const candidate = raw as ShopAttribute;
  const name = candidate.name;
  const image = typeof candidate.image === 'string' ? candidate.image : name ? iconMap[name] ?? null : null;
  return { name, image };
}

function parseAttributeList(raw: unknown, iconMap: Record<string, string>): ShopAttribute[] | undefined {
  if (Array.isArray(raw)) {
    return raw.map((item) => normalizeAttribute(item, iconMap));
  }
  if (typeof raw === 'string') {
    const names = raw.split(',').map((name) => name.trim()).filter(Boolean);
    return names.length ? names.map((name) => ({ name, image: iconMap[name] ?? null })) : undefined;
  }
  return undefined;
}

function parseServices(raw: unknown): ShopAttribute[] | undefined {
  if (Array.isArray(raw)) return raw as ShopAttribute[];
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as ShopAttribute[]) : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export function normalizeShop(raw: unknown): Shop {
  if (!raw || typeof raw !== 'object') return {};
  const shop = raw as Shop & Record<string, unknown>;
  return {
    ...shop,
    services: parseServices(shop.services),
    amenities: parseAttributeList(shop.amenities, config.ASSETS.AMENITY_ICONS),
    brands: parseAttributeList(shop.brands, config.ASSETS.BRAND_LOGOS),
  };
}

export function normalizeShops(raw: unknown): Shop[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((item) => normalizeShop(item));
}
